import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {catchError, debounceTime, distinctUntilChanged, filter, map, of, Subscription, switchMap} from 'rxjs';
import {SearchSuggestionModel} from '@ee/common/models';
import {SearchService} from '@ee/common/services';
import {SearchResultType} from '@ee/common/enums';
import {TagManagerModule} from '@ee/common/tag-manager';

/**
 * Typeahead eviction picker backed by the global search suggestions endpoint
 * (`GET /api/search/suggestions?types=EVICTION`), so it matches by case ID, case name,
 * tenant names, property address, docket number, and tags. For eviction suggestions
 * `display` is the case_id and `description` is a "tenants | address | unit | docket" line.
 *
 * Results render in a `mat-autocomplete` dropdown (floats over the page, so it never resizes its
 * host — important inside dialogs like Link Eviction). Picks show as chips INSIDE the control,
 * styled like the reports "Tags" selector.
 * - Single-select (default): holds one chip; picking replaces it and emits via `selectionChange`
 *   (null when the chip is removed).
 * - Multi-select (`multiSelect=true`): accumulates chips and emits the full list via
 *   `selectionsChange`. Used by the Split/Duplicate billing case picker (EE-181).
 *
 * Rendering has two modes, sharing the same search/selection logic and results panel below:
 * - Default: the bespoke chip-holding box described above.
 * - `matOutline=true`: a real `mat-form-field appearance="outline" class="compact full-width"` +
 *   `matInput`, matching sibling Material fields exactly (e.g. the Inbox's "Link to existing case(s)"
 *   next to "Add a document" — same global `.compact`/`.full-width` classes, not CSS trying to
 *   approximate Material's look). Chips are never rendered in this mode (a plain input has no room
 *   for them) — pair with `hideSelectedChips` and render the current selection elsewhere.
 */
@Component({
  selector: 'ee-eviction-search-select',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    TagManagerModule
  ],
  template: `
    @if (matOutline) {
      <mat-form-field appearance="outline" class="compact full-width">
        @if (label) {
          <mat-label>{{ label }}</mat-label>
        }
        <input matInput [formControl]="searchControl" [placeholder]="placeholder" autocomplete="off"
          [attr.aria-label]="label || placeholder"
          [matAutocomplete]="auto"
          (keydown.backspace)="onBackspace()">
        @if (searching) {
          <mat-spinner matSuffix diameter="16"></mat-spinner>
        } @else if (showSearchIcon) {
          <mat-icon matSuffix fontSet="fa" fontIcon="fa-magnifying-glass"></mat-icon>
        }
      </mat-form-field>
    } @else {
    <div class="ee-search-field">
      @if (label) {
        <span class="ee-search-label">{{ label }}</span>
      }
      <div class="ee-search-control" matAutocompleteOrigin #origin="matAutocompleteOrigin">
        @if (!hideSelectedChips) {
          @if (fixedChipLabel) {
            <span class="ee-tag-chip ee-tag-chip--fixed">{{ fixedChipLabel }}</span>
          }
          @for (sel of selectedList; track sel.display) {
            <span class="ee-tag-chip">
              {{ sel.display }}
              <button type="button" class="ee-tag-chip__remove" (click)="removeSelection(sel)"
                [attr.aria-label]="'Remove ' + sel.display">
                <mat-icon fontSet="fa" fontIcon="fa-xmark"></mat-icon>
              </button>
            </span>
          }
        }
        <input [formControl]="searchControl" [placeholder]="(!hideSelectedChips && selectedList.length) ? '' : placeholder" autocomplete="off"
          [attr.aria-label]="label || placeholder"
          [matAutocomplete]="auto" [matAutocompleteConnectedTo]="origin"
          (keydown.backspace)="onBackspace()">
        @if (searching) {
          <mat-spinner class="ee-search-spinner" diameter="16"></mat-spinner>
        } @else if (showSearchIcon) {
          <mat-icon class="ee-search-icon" fontSet="fa" fontIcon="fa-magnifying-glass"></mat-icon>
        }
      </div>
    </div>
    }

    <mat-autocomplete #auto="matAutocomplete" class="ee-eviction-search-panel"
      [displayWith]="displayFn" (optionSelected)="select($event.option.value)">
      @if (!searching && searched && results.length === 0) {
        <mat-option disabled>No evictions found.</mat-option>
      }
      @for (result of results; track result.display) {
        <mat-option [value]="result">
          <div class="result-row" [class.selected]="isSelected(result)">
            <mat-icon class="check-icon" fontSet="fa" [fontIcon]="iconFor(result)"></mat-icon>
            <div class="result-info">
              <div class="result-title-row">
                <span class="result-case-id">{{ result.display }}</span>
                @if (result.status === 'Closed') {
                  <ee-tag [tag]="{name: result.status, type: 'client', color: 'red'}"
                    [noWrap]="true" size="small"></ee-tag>
                }
              </div>
              @if (result.description) {
                <div class="result-description">{{ result.description }}</div>
              }
            </div>
          </div>
        </mat-option>
      }
    </mat-autocomplete>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .ee-search-label {
        display: block;
        font-size: 12px;
        color: var(--color-on-surface-variant, rgba(0, 0, 0, 0.6));
        margin-bottom: 4px;
      }

      /* Bordered control that holds the chips + search input, matching the "Tags" selector look. */
      .ee-search-control {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        border: 1px solid var(--color-outline, #cbcbcb);
        border-radius: 4px;
        padding: 6px 8px;
        min-height: 42px;
        background-color: var(--color-surface, #ffffff);

        &:focus-within {
          border-color: var(--color-on-surface-variant, #616161);
        }

        input {
          flex: 1 1 120px;
          min-width: 120px;
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          padding: 2px 0;
          color: inherit;
        }

        .ee-search-spinner {
          flex-shrink: 0;
        }

        .ee-search-icon {
          flex-shrink: 0;
          font-size: 16px;
          width: 16px;
          height: 16px;
          line-height: 16px;
          color: var(--color-on-surface-variant, rgba(0, 0, 0, 0.6));
        }
      }

      /* Selected-value chip styled to match the reports "Tags" selector (light-blue tagify look). */
      .ee-tag-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border-radius: 4px;
        padding: 2px 6px;
        font-size: 13px;
        font-weight: 600;
        color: var(--dashboard-blue);
        background-color: var(--c-light-blue);

        /* Non-removable "This case (current)" indicator — neutral gray, like the client gray label. */
        &.ee-tag-chip--fixed {
          color: var(--dashboard-gray, rgba(0, 0, 0, 0.6));
          background-color: var(--c-light-gray, rgba(0, 0, 0, 0.1));
        }

        .ee-tag-chip__remove {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          cursor: pointer;
          padding: 0;
          color: var(--dashboard-blue);

          mat-icon {
            font-size: 14px;
            width: 14px;
            height: 14px;
            line-height: 14px;
          }
        }
      }

      .result-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;

        .check-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          line-height: 18px;
          overflow: visible;
          flex-shrink: 0;
          color: var(--color-on-surface-variant, #bbb);
        }

        &.selected .check-icon {
          color: var(--primary-red, #c62828);
        }

        .result-info {
          display: flex;
          flex-direction: column;
          line-height: 1.3;
          min-width: 0;

          .result-title-row {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;

            .result-case-id {
              font-weight: 600;
            }
          }

          .result-description {
            font-size: 12px;
            color: var(--color-on-surface-variant, #777);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    `
  ]
})
export class EvictionSearchSelectComponent implements OnInit, OnDestroy {
  /** Caption shown above the control. Pass '' to omit it entirely (e.g. to match a shorter sibling
   * field's box height when placed alongside one) — rely on `placeholder` alone in that case. */
  @Input() label = 'Search Evictions';
  @Input() placeholder = 'Case ID, tenant, case name, or address...';
  @Input() excludeCaseIds: string[] = [];
  /** Optional read-only leading chip (e.g. "This case (current)") shown inside the control. */
  @Input() fixedChipLabel: string | null = null;
  /** Case ids to pre-populate as chips (multi-select), e.g. rows checked in a grid. */
  @Input() initialCaseIds: string[] = [];
  /** When true, allow selecting multiple cases and emit via `selectionsChange`. */
  @Input() multiSelect = false;
  /**
   * When true, suppress the selected-chip display inside the control — use when the consumer
   * already renders the current selection elsewhere (e.g. a list with per-case detail below) and
   * showing it a second time inside the search box would be redundant. The box then behaves as a
   * pure search/add input: it always shows its placeholder and never grows with picked chips.
   * Selection/toggle behavior (including the checked icon on already-picked results) is unaffected.
   */
  @Input() hideSelectedChips = false;
  /** When true, show a trailing search (magnifying-glass) icon, hidden while a search is in flight
   * (the spinner takes its place). Off by default so existing consumers are unaffected. */
  @Input() showSearchIcon = false;
  /** When true, render as a real `mat-form-field appearance="outline"` instead of the bespoke
   * chip-holding box — see the class doc. Off by default so existing consumers are unaffected. */
  @Input() matOutline = false;
  @Output() selectionChange = new EventEmitter<SearchSuggestionModel | null>();
  /** Emits the full list of picked suggestions on every add/remove (multi-select mode only). */
  @Output() selectionsChange = new EventEmitter<SearchSuggestionModel[]>();

  searchControl = new FormControl('');
  results: SearchSuggestionModel[] = [];
  selectedList: SearchSuggestionModel[] = [];
  searching = false;
  searched = false;

  private sub?: Subscription;

  constructor(private searchService: SearchService) {}

  ngOnInit(): void {
    if (this.initialCaseIds.length) {
      this.selectedList = this.initialCaseIds.map(id => ({
        fontIcon: 'fa-briefcase',
        type: SearchResultType.EVICTION,
        display: id,
        path: `/evictions/${encodeURIComponent(id)}`,
        description: '',
        status: null,
        tags: []
      }));
    }

    this.sub = this.searchControl.valueChanges.pipe(
      // Ignore the object value mat-autocomplete writes when an option is picked.
      filter((value): value is string => typeof value === 'string'),
      debounceTime(300),
      map(value => value.trim()),
      distinctUntilChanged(),
      switchMap(term => {
        if (term.length < 2) {
          this.searched = false;
          return of(null);
        }
        this.searching = true;
        return this.searchService.suggestions(term, [SearchResultType.EVICTION]).pipe(
          catchError(() => of([] as SearchSuggestionModel[]))
        );
      })
    ).subscribe(results => {
      this.searching = false;
      if (results === null) {
        this.results = [];
      } else {
        this.results = results.filter(s => !this.excludeCaseIds?.includes(s.display));
        this.searched = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Keeps the input empty after mat-autocomplete writes the picked option object. */
  displayFn = (value: SearchSuggestionModel | string | null): string =>
    typeof value === 'string' ? value : '';

  select(result: SearchSuggestionModel): void {
    if (this.multiSelect) {
      this.selectedList = this.isSelected(result)
        ? this.selectedList.filter(s => s.display !== result.display)
        : [...this.selectedList, result];
      this.selectionsChange.emit([...this.selectedList]);
    } else {
      this.selectedList = [result];
      this.selectionChange.emit(result);
    }
    // Reset the search box so the next case can be looked up (without re-triggering a search).
    this.searchControl.setValue('', {emitEvent: false});
    this.results = [];
    this.searched = false;
  }

  removeSelection(result: SearchSuggestionModel): void {
    this.selectedList = this.selectedList.filter(s => s.display !== result.display);
    if (this.multiSelect) {
      this.selectionsChange.emit([...this.selectedList]);
    } else {
      this.selectionChange.emit(null);
    }
  }

  /**
   * Backspace on an empty input removes the last chip (standard tag-input behavior). Skipped when
   * chips are hidden, or in `matOutline` mode (which never renders chips regardless of
   * `hideSelectedChips`) — silently removing a selection with no visible chip to disappear would be
   * confusing; removal is only available through whatever list the consumer renders instead.
   */
  onBackspace(): void {
    if (!this.hideSelectedChips && !this.matOutline && !this.searchControl.value && this.selectedList.length) {
      this.removeSelection(this.selectedList[this.selectedList.length - 1]);
    }
  }

  isSelected(result: SearchSuggestionModel): boolean {
    return this.selectedList.some(s => s.display === result.display);
  }

  iconFor(result: SearchSuggestionModel): string {
    if (this.multiSelect) {
      return this.isSelected(result) ? 'fa-square-check' : 'fa-square';
    }
    return this.isSelected(result) ? 'fa-circle-check' : 'fa-circle';
  }
}
